import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Edit3, Save, X, ChevronDown, ChevronUp, FileText, Image as ImageIcon,
  Package, ShieldCheck, Rocket, ExternalLink, AlertCircle,
} from 'lucide-react';
import theme from '../theme';
import KickbackOfferEvidence from '../components/KickbackOfferEvidence';
import NFTGallery from '../components/NFTGallery';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Batch 1C-B1 Part 1 — dedicated Edit Kickback page rebuilt as a real
 * section-based editor. Post-initial-setup Kickbacks land here (NOT the
 * Create Kickback wizard). Each section supports inline View / Edit /
 * Save / Cancel. Lifecycle fields (initial_setup_completed*,
 * agreement_accepted*) are NEVER sent from this page.
 */

const OFFER_TYPE_LABEL = {
  physical_product: 'Physical Product',
  digital_product: 'Digital Product',
  service_program: 'Service / Course / Program',
  company_employee: 'Company / Employee Kickback',
  event_experience: 'Event / Experience',
  other: 'Other',
};
const MGMT_LABEL = {
  diy: 'DIY / Self-Managed', self: 'DIY / Self-Managed', creator: 'DIY / Self-Managed',
  curator: 'Kickback Architect Managed', architect: 'Kickback Architect Managed', broker: 'Kickback Architect Managed',
};
const STANDARD_LABEL = { ERC721: 'ERC-721', ERC1155: 'ERC-1155', ERC20: 'ERC-20' };
const humanOffer = v => OFFER_TYPE_LABEL[(v || '').toLowerCase()] || (v || '—');
const humanMgmt = v => MGMT_LABEL[(v || '').toLowerCase()] || (v || '—');
const humanStd = v => (v ? (STANDARD_LABEL[v.toUpperCase()] || v) : '—');

const CATEGORY_OPTIONS = [
  'Apparel', 'Art', 'Music', 'Gaming', 'Sports', 'Collectibles',
  'Photography', 'Virtual Worlds', 'Trading Cards', 'Domain Names', 'Other',
];
const KICKBACK_TYPES = [
  { id: 'revenue_share',  label: 'Revenue Share' },
  { id: 'royalty_split',  label: 'Royalty Split' },
  { id: 'affiliate_fee',  label: 'Affiliate Fee' },
  { id: 'referral_bonus', label: 'Referral Bonus' },
  { id: 'event_perk',     label: 'Event Perk' },
];

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.md, marginBottom: 14,
};
const sectionHeader = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', cursor: 'pointer' };
const sectionBody = { padding: '4px 16px 16px', borderTop: `1px solid ${theme.colors.borders}` };
const labelStyle = { fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 };
const valueStyle = { fontSize: 13, fontWeight: 600, color: theme.colors.text.primary };
const inputStyle = { width: '100%', padding: '8px 10px', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm, fontSize: 13, color: theme.colors.text.primary, background: '#FFF' };
const btnPrimary = { padding: '7px 14px', border: 'none', background: theme.colors.primary, color: '#FFF', borderRadius: theme.radius.sm, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnSecondary = { padding: '7px 14px', border: `1px solid ${theme.colors.borders}`, background: '#FFF', color: theme.colors.text.primary, borderRadius: theme.radius.sm, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };

const KickbackEditPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [draft, setDraft] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => searchParams.get('section') || 'overview');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await axios.get(`${API}/creator/portfolios/${id}`, auth);
      setPortfolio(p.data.portfolio);
      if (p.data.portfolio?.contract_id) {
        try {
          const d = await axios.get(`${API}/contracts/drafts/${p.data.portfolio.contract_id}`, auth);
          setDraft(d.data.draft || d.data);
        } catch (_) { setDraft(null); }
      }
      const r = await axios.get(`${API}/creator/portfolios/${id}/readiness`, auth);
      setReadiness(r.data.readiness);
    } finally {
      setLoading(false);
    }
  }, [id, auth]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const s = searchParams.get('section');
    if (s) setExpanded(s);
  }, [searchParams]);

  const isCompletedSetup = !!(portfolio?.initial_setup_completed || readiness?.initial_setup_completed);

  const patchPortfolio = async (payload) => {
    setSaving(true); setError(null);
    try {
      const resp = await axios.patch(`${API}/portfolios/${id}`, payload, auth);
      setPortfolio(resp.data.portfolio);
      // Refetch readiness so summary reflects reality.
      const r = await axios.get(`${API}/creator/portfolios/${id}/readiness`, auth);
      setReadiness(r.data.readiness);
      setEditing(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const patchDraft = async (params) => {
    if (!draft?.id) return;
    if (draft.deployed) { setError('Contract is already deployed; edits are not allowed.'); return; }
    setSaving(true); setError(null);
    try {
      const resp = await axios.patch(`${API}/contracts/drafts/${draft.id}`, { params }, auth);
      setDraft(resp.data.draft || { ...draft, params: { ...draft.params, ...params } });
      setEditing(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Contract update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Loading…</div>;
  if (!portfolio) return <div style={{ padding: 60, textAlign: 'center' }}>Kickback not found.</div>;

  // Incomplete Kickbacks: keep Resume Setup as the canonical workflow (Part 10).
  if (!isCompletedSetup) {
    return (
      <div data-testid="kickback-edit-page-incomplete" style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px' }}>
        <button onClick={() => navigate(`/creator/portfolios/${id}`)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: 14, fontWeight: 600, marginBottom: 20 }}>
          <ArrowLeft size={16} /> Back to Kickback
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: theme.colors.text.primary }}>Finish Setup First</h1>
        <p style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 20 }}>
          This Kickback has not completed its initial 5-step setup yet. Continue where you left off — the section-based editor is available once initial setup is complete.
        </p>
        <button data-testid="edit-resume-setup-btn" onClick={() => navigate(`/creator/portfolios/new?edit=${id}`)} style={btnPrimary}>
          <Edit3 size={14} /> Resume Setup
        </button>
      </div>
    );
  }

  const openSection = (key) => {
    setExpanded(key);
    setEditing(null);
    const next = new URLSearchParams(searchParams); next.set('section', key); setSearchParams(next, { replace: true });
  };
  const toggleSection = (key) => (expanded === key ? openSection('') : openSection(key));

  return (
    <div data-testid="kickback-edit-page" style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
      <button onClick={() => navigate(`/creator/portfolios/${id}`)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: 14, fontWeight: 600, marginBottom: 20 }}>
        <ArrowLeft size={16} /> Back to Kickback
      </button>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 6 }}>Edit Kickback</h1>
      <p style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 20 }}>
        Review and update sections without cycling through the Create Kickback wizard.
      </p>
      {error && <div data-testid="edit-error" style={{ padding: 10, marginBottom: 12, background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#7F1D1D', borderRadius: theme.radius.sm, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}><AlertCircle size={14} /> {String(error)}</div>}

      <OverviewSection portfolio={portfolio} expanded={expanded === 'overview'} onToggle={() => toggleSection('overview')} editing={editing === 'overview'} onEdit={() => setEditing('overview')} onCancel={() => setEditing(null)} onSave={patchPortfolio} saving={saving} />

      <EvidenceSection portfolio={portfolio} expanded={expanded === 'evidence'} onToggle={() => toggleSection('evidence')} editing={editing === 'evidence'} onEdit={() => setEditing('evidence')} onCancel={() => setEditing(null)} onSave={patchPortfolio} saving={saving} />

      <ArtworkSection portfolio={portfolio} expanded={expanded === 'artwork'} onToggle={() => toggleSection('artwork')} editing={editing === 'artwork'} onEdit={() => setEditing('artwork')} onCancel={() => setEditing(null)} onSave={patchPortfolio} saving={saving} />

      <ContractSection portfolio={portfolio} draft={draft} expanded={expanded === 'contract'} onToggle={() => toggleSection('contract')} editing={editing === 'contract'} onEdit={() => setEditing('contract')} onCancel={() => setEditing(null)} onSaveDraft={patchDraft} saving={saving} />

      <ManagementSection portfolio={portfolio} expanded={expanded === 'management'} onToggle={() => toggleSection('management')} editing={editing === 'management'} onEdit={() => setEditing('management')} onCancel={() => setEditing(null)} onSave={patchPortfolio} saving={saving} />

      <LaunchSummarySection portfolioId={id} readiness={readiness} expanded={expanded === 'launch'} onToggle={() => toggleSection('launch')} />
    </div>
  );
};

/* ---------------- Section: Overview ---------------- */
function OverviewSection({ portfolio, expanded, onToggle, editing, onEdit, onCancel, onSave, saving }) {
  const [f, setF] = useState(null);
  useEffect(() => { if (editing) setF({
    name: portfolio.name || '', description: portfolio.description || '', category: portfolio.category || '',
    portfolio_type_ids: portfolio.portfolio_type_ids || [], quantity: portfolio.quantity || 0, unit_price: portfolio.unit_price || 0,
  }); }, [editing, portfolio]);
  const summary = `${portfolio.name || '—'} · ${portfolio.category || '—'}`;
  const humanTypes = (portfolio.portfolio_type_ids || []).map(id => KICKBACK_TYPES.find(t => t.id === id)?.label || id).join(', ') || (portfolio.portfolio_type || '—');
  return (
    <div data-testid="edit-section-overview" style={cardStyle}>
      <div style={sectionHeader} onClick={onToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FileText size={16} color={theme.colors.primary} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary }}>Kickback Overview</div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted }}>{summary}</div>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      {expanded && (
        <div style={sectionBody}>
          {!editing && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
                <div><div style={labelStyle}>Name</div><div style={valueStyle}>{portfolio.name || '—'}</div></div>
                <div><div style={labelStyle}>Category</div><div style={valueStyle}>{portfolio.category || '—'}</div></div>
                <div><div style={labelStyle}>Kickback Types</div><div style={valueStyle}>{humanTypes}</div></div>
                <div><div style={labelStyle}>Quantity</div><div style={valueStyle}>{portfolio.quantity || 0}</div></div>
                <div><div style={labelStyle}>Unit Price</div><div style={valueStyle}>{portfolio.unit_price || 0}</div></div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>Description</div>
                <div style={{ fontSize: 13, color: theme.colors.text.primary, whiteSpace: 'pre-wrap' }}>{portfolio.description || '—'}</div>
              </div>
              <button data-testid="edit-overview-btn" style={btnSecondary} onClick={onEdit}><Edit3 size={12} /> Edit Overview</button>
            </>
          )}
          {editing && f && (
            <div>
              <div style={{ marginBottom: 10 }}><div style={labelStyle}>Name</div><input data-testid="overview-name" style={inputStyle} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
              <div style={{ marginBottom: 10 }}><div style={labelStyle}>Description</div><textarea data-testid="overview-description" style={{ ...inputStyle, minHeight: 90 }} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></div>
              <div style={{ marginBottom: 10 }}>
                <div style={labelStyle}>Category</div>
                <select data-testid="overview-category" style={inputStyle} value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>
                  <option value="">Choose…</option>
                  {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={labelStyle}>Kickback Types</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {KICKBACK_TYPES.map(t => {
                    const on = f.portfolio_type_ids.includes(t.id);
                    return (
                      <button key={t.id} type="button" data-testid={`overview-type-${t.id}`}
                        onClick={() => setF({ ...f, portfolio_type_ids: on ? f.portfolio_type_ids.filter(x => x !== t.id) : [...f.portfolio_type_ids, t.id] })}
                        style={{ padding: '6px 10px', borderRadius: theme.radius.sm, border: `1px solid ${on ? theme.colors.primary : theme.colors.borders}`, background: on ? `${theme.colors.primary}14` : '#FFF', color: on ? theme.colors.primary : theme.colors.text.primary, fontSize: 12, cursor: 'pointer' }}>{t.label}</button>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 12 }}>
                <div><div style={labelStyle}>Quantity</div><input data-testid="overview-quantity" type="number" style={inputStyle} value={f.quantity} onChange={e => setF({ ...f, quantity: Number(e.target.value) })} /></div>
                <div><div style={labelStyle}>Unit Price</div><input data-testid="overview-unit-price" type="number" step="0.0001" style={inputStyle} value={f.unit_price} onChange={e => setF({ ...f, unit_price: Number(e.target.value) })} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button data-testid="overview-save-btn" style={btnPrimary} disabled={saving} onClick={() => onSave({
                  name: f.name.trim(), description: f.description, category: f.category, portfolio_type: f.portfolio_type_ids[0] || 'Revenue Sharing',
                  portfolio_type_ids: f.portfolio_type_ids, quantity: f.quantity, unit_price: f.unit_price,
                })}><Save size={12} /> {saving ? 'Saving…' : 'Save Changes'}</button>
                <button data-testid="overview-cancel-btn" style={btnSecondary} disabled={saving} onClick={onCancel}><X size={12} /> Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Section: Evidence ---------------- */
function EvidenceSection({ portfolio, expanded, onToggle, editing, onEdit, onCancel, onSave, saving }) {
  const [ev, setEv] = useState(null);
  useEffect(() => { if (editing) setEv(portfolio.offer_evidence || { offer_type: '', fields: {}, uploads: {} }); }, [editing, portfolio]);
  const evNow = portfolio.offer_evidence || {};
  const uploads = evNow.uploads || {};
  const cnt = k => (Array.isArray(uploads[k]) ? uploads[k].length : 0);
  return (
    <div data-testid="edit-section-evidence" style={cardStyle}>
      <div style={sectionHeader} onClick={onToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ShieldCheck size={16} color={theme.colors.primary} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary }}>Item / Offer Evidence</div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted }}>{humanOffer(evNow.offer_type)}</div>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      {expanded && (
        <div style={sectionBody}>
          {!editing && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
                <div><div style={labelStyle}>Offer Type</div><div style={valueStyle}>{humanOffer(evNow.offer_type)}</div></div>
                <div><div style={labelStyle}>Product / Offer Images</div><div style={valueStyle}>{cnt('product_offer_images')}</div></div>
                <div><div style={labelStyle}>Unique Markings / Proof Images</div><div style={valueStyle}>{cnt('unique_markings_images')}</div></div>
                <div><div style={labelStyle}>Design / Diagram Images</div><div style={valueStyle}>{cnt('design_placement_images')}</div></div>
                <div><div style={labelStyle}>Supporting Documents</div><div style={valueStyle}>{cnt('supporting_documents')}</div></div>
              </div>
              <button data-testid="edit-evidence-btn" style={btnSecondary} onClick={onEdit}><Edit3 size={12} /> Edit Evidence</button>
            </>
          )}
          {editing && ev && (
            <div>
              <KickbackOfferEvidence value={ev} onChange={setEv} />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button data-testid="evidence-save-btn" style={btnPrimary} disabled={saving} onClick={() => onSave({ offer_evidence: ev })}><Save size={12} /> {saving ? 'Saving…' : 'Save Evidence'}</button>
                <button data-testid="evidence-cancel-btn" style={btnSecondary} disabled={saving} onClick={onCancel}><X size={12} /> Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Section: Artwork ---------------- */
function ArtworkSection({ portfolio, expanded, onToggle, editing, onEdit, onCancel, onSave, saving }) {
  const [ids, setIds] = useState(portfolio.nft_asset_ids || []);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [galleryRefresh, setGalleryRefresh] = useState(0);
  useEffect(() => { if (editing) setIds(portfolio.nft_asset_ids || []); }, [editing, portfolio]);

  // Batch 1C-B1 FINAL Part 1 — Upload New Artwork wired to the existing
  // production artwork endpoint. New assets are classified kind='artwork'
  // and appear in the Artwork Library.
  const uploadArtwork = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true); setUploadError(null);
    const token = localStorage.getItem('token');
    const newIds = [];
    for (const f of Array.from(fileList)) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('name', f.name);
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nft/upload-single`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
        });
        if (!r.ok) throw new Error('upload_failed');
        const j = await r.json();
        const id = j.nft_id || j.file_id || j.id;
        if (id) newIds.push(id);
      } catch (_) { setUploadError('One or more artwork files failed to upload.'); }
    }
    if (newIds.length) {
      // Batch 1C-B1 FINAL SOURCE-OF-TRUTH — Upload MUST NOT auto-select.
      // Newly uploaded artwork lands in the Artwork Library only. The
      // Creator explicitly selects it via the gallery toggle. The
      // canonical selected set is `portfolios.nft_asset_ids` which is
      // only mutated on Save Selection.
    }
    // Trigger NFTGallery refresh so the newly uploaded assets appear
    setGalleryRefresh(v => v + 1);
    setUploading(false);
  };

  return (
    <div data-testid="edit-section-artwork" style={cardStyle}>
      <div style={sectionHeader} onClick={onToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ImageIcon size={16} color={theme.colors.primary} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary }}>Kickback Artwork</div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted }}>{(portfolio.nft_asset_ids || []).length} selected</div>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      {expanded && (
        <div style={sectionBody}>
          {!editing && (
            <>
              <div style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 12 }}>
                {(portfolio.nft_asset_ids || []).length === 0 ? 'No Kickback Artwork selected yet.' : `${(portfolio.nft_asset_ids || []).length} Kickback Artwork asset(s) attached.`}
              </div>
              <button data-testid="edit-artwork-btn" style={btnSecondary} onClick={onEdit}><Edit3 size={12} /> Manage Kickback Artwork</button>
            </>
          )}
          {editing && (
            <div>
              <div style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>Upload New Artwork</div>
              <label data-testid="artwork-upload-label" style={{ ...btnSecondary, display: 'inline-flex', cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.7 : 1, marginBottom: 10 }}>
                <input data-testid="artwork-upload-input" type="file" accept="image/*" multiple style={{ display: 'none' }}
                  onChange={e => { uploadArtwork(e.target.files); e.target.value = ''; }} />
                {uploading ? 'Uploading…' : 'Upload New Artwork'}
              </label>
              {uploadError && <div data-testid="artwork-upload-error" style={{ fontSize: 12, color: '#B91C1C', marginBottom: 8 }}>{uploadError}</div>}
              <p style={{ fontSize: 11, color: theme.colors.text.muted, marginBottom: 12 }}>New artwork is stored in your Artwork Library and can be reused across Kickbacks. AI Generate is temporarily unavailable.</p>
              <div style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>Choose From Artwork Library</div>
              <NFTGallery key={galleryRefresh} selectedIds={ids} onSelect={setIds} allowMultiple />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button data-testid="artwork-save-btn" style={btnPrimary} disabled={saving} onClick={() => onSave({ nft_asset_ids: ids })}><Save size={12} /> {saving ? 'Saving…' : 'Save Selection'}</button>
                <button data-testid="artwork-cancel-btn" style={btnSecondary} disabled={saving} onClick={onCancel}><X size={12} /> Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Section: Contract Draft ---------------- */
function ContractSection({ portfolio, draft, expanded, onToggle, editing, onEdit, onCancel, onSaveDraft, saving }) {
  const [p, setP] = useState({});
  useEffect(() => { if (editing && draft) setP({ ...(draft.params || {}) }); }, [editing, draft]);
  const params = draft?.params || {};
  const deployed = draft?.deployed;
  return (
    <div data-testid="edit-section-contract" style={cardStyle}>
      <div style={sectionHeader} onClick={onToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FileText size={16} color={theme.colors.primary} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary }}>Contract Draft</div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted }}>{draft ? `${humanStd(draft.standard)} · ${deployed ? 'Deployed' : 'Draft'}` : 'No contract draft'}</div>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      {expanded && (
        <div style={sectionBody}>
          {!draft && (
            <div>
              <p style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 12 }}>No contract draft is linked to this Kickback yet. Configure one in the dedicated Contract workspace.</p>
              <button data-testid="contract-open-workspace" style={btnPrimary} onClick={() => window.location.assign('/creator/contracts/new')}><ExternalLink size={12} /> Open Contract Workspace</button>
            </div>
          )}
          {draft && !editing && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
                <div><div style={labelStyle}>Contract Standard</div><div style={valueStyle}>{humanStd(draft.standard)}</div></div>
                <div><div style={labelStyle}>Token Name</div><div style={valueStyle}>{params.token_name || params.name || params.contract_name || '—'}</div></div>
                <div><div style={labelStyle}>Token Symbol</div><div style={valueStyle}>{params.token_symbol || params.symbol || '—'}</div></div>
                <div><div style={labelStyle}>Maximum Supply</div><div style={valueStyle}>{params.max_supply || '—'}</div></div>
                <div><div style={labelStyle}>Mint Price</div><div style={valueStyle}>{params.mint_price || '—'}</div></div>
                <div><div style={labelStyle}>Royalty</div><div style={valueStyle}>{params.royalty_percent || '—'}</div></div>
                <div style={{ gridColumn: '1/-1' }}><div style={labelStyle}>Base URI / Metadata</div><div style={valueStyle}>{params.base_uri || '—'}</div></div>
                <div><div style={labelStyle}>Draft Status</div><div style={valueStyle}>{draft.status || 'DRAFT'}</div></div>
                <div><div style={labelStyle}>Generated Code</div><div style={valueStyle}>{draft.generated_code ? 'Ready' : 'Missing'}</div></div>
                <div><div style={labelStyle}>Deployment</div><div style={valueStyle}>{deployed ? 'Deployed' : 'Not Deployed'}</div></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!deployed && <button data-testid="contract-edit-inline" style={btnSecondary} onClick={onEdit}><Edit3 size={12} /> Edit Draft Fields</button>}
                <button data-testid="contract-open-workspace" style={btnSecondary} onClick={() => window.location.assign(`/creator/contracts/${draft.id}?from=edit&portfolio_id=${portfolio.id}`)}><ExternalLink size={12} /> Open Contract Workspace</button>
              </div>
              {deployed && <p style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 8 }}>This contract is deployed on-chain. Destructive fields are immutable.</p>}
            </>
          )}
          {draft && editing && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
                {['token_name', 'name', 'contract_name'].filter(k => params[k] !== undefined || p[k] !== undefined).slice(0, 1).map(k => (
                  <div key={k}><div style={labelStyle}>Token Name</div><input data-testid={`contract-${k}`} style={inputStyle} value={p[k] || ''} onChange={e => setP({ ...p, [k]: e.target.value })} /></div>
                ))}
                {['token_symbol', 'symbol'].filter(k => params[k] !== undefined || p[k] !== undefined).slice(0, 1).map(k => (
                  <div key={k}><div style={labelStyle}>Token Symbol</div><input data-testid={`contract-${k}`} style={inputStyle} value={p[k] || ''} onChange={e => setP({ ...p, [k]: e.target.value })} /></div>
                ))}
                {params.max_supply !== undefined && <div><div style={labelStyle}>Maximum Supply</div><input data-testid="contract-max-supply" style={inputStyle} value={p.max_supply || ''} onChange={e => setP({ ...p, max_supply: e.target.value })} /></div>}
                {params.mint_price !== undefined && <div><div style={labelStyle}>Mint Price</div><input data-testid="contract-mint-price" style={inputStyle} value={p.mint_price || ''} onChange={e => setP({ ...p, mint_price: e.target.value })} /></div>}
                {params.base_uri !== undefined && <div style={{ gridColumn: '1/-1' }}><div style={labelStyle}>Base URI / Metadata</div><input data-testid="contract-base-uri" style={inputStyle} value={p.base_uri || ''} onChange={e => setP({ ...p, base_uri: e.target.value })} /></div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button data-testid="contract-save-inline" style={btnPrimary} disabled={saving} onClick={() => onSaveDraft(p)}><Save size={12} /> {saving ? 'Saving…' : 'Save Draft Changes'}</button>
                <button data-testid="contract-cancel-inline" style={btnSecondary} disabled={saving} onClick={onCancel}><X size={12} /> Cancel</button>
              </div>
              <p style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 8 }}>Edits update the same canonical draft (no new draft is created, no additional quota is consumed).</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Section: Management & Terms ---------------- */
function ManagementSection({ portfolio, expanded, onToggle, editing, onEdit, onCancel, onSave, saving }) {
  const [f, setF] = useState(null);
  useEffect(() => { if (editing) setF({
    management_type: portfolio.management_type || '', kickback_percent: portfolio.kickback_percent ?? '',
    quantity: portfolio.quantity ?? '', unit_price: portfolio.unit_price ?? '',
  }); }, [editing, portfolio]);
  const humanMg = humanMgmt(portfolio.management_type);
  return (
    <div data-testid="edit-section-management" style={cardStyle}>
      <div style={sectionHeader} onClick={onToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Package size={16} color={theme.colors.primary} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary }}>Management & Terms</div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted }}>{humanMg} · {portfolio.kickback_percent == null ? 'Not set' : `${portfolio.kickback_percent}% Kickback`}</div>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      {expanded && (
        <div style={sectionBody}>
          {!editing && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
                <div><div style={labelStyle}>Management Type</div><div style={valueStyle}>{humanMg}</div></div>
                <div><div style={labelStyle}>Kickback Percentage</div><div style={valueStyle}>{portfolio.kickback_percent == null ? 'Not set' : `${portfolio.kickback_percent}% Kickback`}</div></div>
                <div><div style={labelStyle}>Quantity</div><div style={valueStyle}>{portfolio.quantity == null ? 'Not set' : portfolio.quantity}</div></div>
                <div><div style={labelStyle}>Unit Price</div><div style={valueStyle}>{portfolio.unit_price == null ? 'Not set' : portfolio.unit_price}</div></div>
              </div>
              <button data-testid="edit-management-btn" style={btnSecondary} onClick={onEdit}><Edit3 size={12} /> Edit Management & Terms</button>
            </>
          )}
          {editing && f && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
                <div>
                  <div style={labelStyle}>Management Type</div>
                  <select data-testid="mgmt-type" style={inputStyle} value={f.management_type} onChange={e => setF({ ...f, management_type: e.target.value })}>
                    <option value="diy">DIY / Self-Managed</option>
                    <option value="curator">Kickback Architect Managed</option>
                  </select>
                </div>
                <div><div style={labelStyle}>Kickback %</div><input data-testid="mgmt-percent" type="number" step="0.01" style={inputStyle} value={f.kickback_percent} onChange={e => setF({ ...f, kickback_percent: Number(e.target.value) })} /></div>
                <div><div style={labelStyle}>Quantity</div><input data-testid="mgmt-quantity" type="number" style={inputStyle} value={f.quantity} onChange={e => setF({ ...f, quantity: Number(e.target.value) })} /></div>
                <div><div style={labelStyle}>Unit Price</div><input data-testid="mgmt-unit-price" type="number" step="0.0001" style={inputStyle} value={f.unit_price} onChange={e => setF({ ...f, unit_price: Number(e.target.value) })} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button data-testid="mgmt-save-btn" style={btnPrimary} disabled={saving} onClick={() => onSave({
                  management_type: f.management_type, kickback_percent: f.kickback_percent, quantity: f.quantity, unit_price: f.unit_price,
                })}><Save size={12} /> {saving ? 'Saving…' : 'Save Changes'}</button>
                <button data-testid="mgmt-cancel-btn" style={btnSecondary} disabled={saving} onClick={onCancel}><X size={12} /> Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Section: Launch Readiness Summary ---------------- */
function LaunchSummarySection({ portfolioId, readiness, expanded, onToggle }) {
  const pct = readiness?.percent ?? 0;
  const done = readiness?.completed_required_count ?? 0;
  const tot = readiness?.required_count ?? 0;
  const blockers = readiness?.blockers?.length ?? 0;
  const isReady = readiness?.ready_to_launch;
  return (
    <div data-testid="edit-section-launch" style={cardStyle}>
      <div style={sectionHeader} onClick={onToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Rocket size={16} color={theme.colors.primary} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary }}>Launch Readiness</div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted }}>{pct}% complete ({done} / {tot} required) · {blockers} blocker(s)</div>
          </div>
        </div>
        <span style={{ padding: '3px 10px', borderRadius: 999, background: isReady ? `${theme.colors.status.success}22` : '#FDF3E7', color: isReady ? theme.colors.status.success : '#8B5A00', fontSize: 11, fontWeight: 700 }}>{isReady ? 'Ready' : 'Not Ready'}</span>
      </div>
      {expanded && (
        <div style={sectionBody}>
          <button data-testid="edit-launch-review-btn" style={btnPrimary} onClick={() => window.location.assign(`/creator/portfolios/${portfolioId}/launch-readiness`)}>
            <Rocket size={12} /> Review Launch Readiness
          </button>
        </div>
      )}
    </div>
  );
}

export default KickbackEditPage;
